import { environment } from '@environment';

export enum TOOLBAR_TYPES {
  LARGE = 'large',
  ILT_EVENT = 'ilt_event',
  DEFAULT = 'default',
}
export function getDefaultFroalaEditorConfig(
  lang = 'en',
  disabledPlugins = ['image', 'templates', 'accordion', 'video'],
  h5p = false,
  options = {},
  toolbar: TOOLBAR_TYPES = TOOLBAR_TYPES.DEFAULT,
): any {
  const path = 'company_news_settings';
  const imageUploadURL = h5p
    ? // @ts-ignore
      globalThis.H5PIntegration.ajaxPath + 'ck-editor-files'
    : `${environment.backendBaseUrl}/api/v1/${path}/upload_wysiwyg_file`;
  const videoUploadURL = h5p
    ? // @ts-ignore
      globalThis.H5PIntegration.ajaxPath + 'ck-editor-files'
    : `${environment.backendBaseUrl}/api/v1/${path}/upload_wysiwyg_file`;

  const quickInsertButtons = [
    'hr',
    'templates',
    'accordion',
    'table',
    'image',
    'video',
  ].filter(btn => !disabledPlugins.some(p => p === btn));

  const toolbarButtons =
    toolbar === TOOLBAR_TYPES.LARGE
      ? largeToolbarConfig(disabledPlugins)
      : toolbar === TOOLBAR_TYPES.ILT_EVENT
        ? iltEventToolbarConfig
        : smallToolbarConfig;

  return {
    enter: 2, // BR Mode
    heightMin: '200px',
    heightMax: '400px',
    language: lang,
    fontFamily: {
      'Arial, Comic Sans MS, cursive': 'Arial',
      'Arial Nova, sans-serif': 'Arial Nova',
      'Barlow, sans-serif': 'Barlow',
      'Comic Sans MS, cursive': 'Comic Sans MS',
      'Courier New, Courier, monospace': 'Courier New',
      'DIN Pro, sans-serif': 'DIN Pro',
      'Georgia, serif': 'Georgia',
      'Host Grotesk, sans-serif': 'Host Grotesk',
      'Lexend, sans-serif': 'Lexend',
      'Lora, sans-serif;': 'Lora',
      'Lucida Sans Unicode, Lucida Sans Unicode, Lucida Grande, sans-serif':
        'Lucida Sans Unicode',
      'Open Sans, sans-serif': 'Open Sans',
      'Palanquin, sans-serif': 'Palanquin',
      'Paul Signature, cursive': 'Paul Signature',
      'Roboto, sans-serif': 'Roboto',
      'Tahoma, Geneva, sans-serif': 'Tahoma',
      'TeXGyreHeros, sans-serif': 'TeXGyreHeros',
      'Times New Roman, Times, serif': 'Times New Roman',
      'Trebuchet MS, Verdana, Geneva, sans-serif': 'Trebuchet MS',
      'Ubuntu, sans-serif': 'Ubuntu',
      'Verdana, Geneva, sans-serif': 'Verdana',
    },
    htmlAllowedAttrs: [
      'accept',
      'accept-charset',
      'accesskey',
      'action',
      'align',
      'allowfullscreen',
      'allowtransparency',
      'alt',
      'aria-.*',
      'async',
      'autocomplete',
      'autofocus',
      'autoplay',
      'autosave',
      'background',
      'bgcolor',
      'border',
      'charset',
      'cellpadding',
      'cellspacing',
      'checked',
      'cite',
      'class',
      'color',
      'cols',
      'colspan',
      'content',
      'contenteditable',
      'contextmenu',
      'controls',
      'coords',
      'data',
      'data-.*',
      'datetime',
      'default',
      'defer',
      'dir',
      'dirname',
      'disabled',
      'download',
      'draggable',
      'dropzone',
      'enctype',
      'for',
      'form',
      'formaction',
      'frameborder',
      'headers',
      'height',
      'hidden',
      'high',
      'href',
      'hreflang',
      'http-equiv',
      'icon',
      'id',
      'ismap',
      'itemprop',
      'keytype',
      'kind',
      'label',
      'lang',
      'language',
      'list',
      'loop',
      'low',
      'max',
      'maxlength',
      'media',
      'method',
      'min',
      'mozallowfullscreen',
      'multiple',
      'muted',
      'name',
      'novalidate',
      'open',
      'optimum',
      'onclick',
      'pattern',
      'ping',
      'placeholder',
      'playsinline',
      'poster',
      'preload',
      'pubdate',
      'radiogroup',
      'readonly',
      'rel',
      'required',
      'reversed',
      'rows',
      'rowspan',
      'sandbox',
      'scope',
      'scoped',
      'scrolling',
      'seamless',
      'selected',
      'shape',
      'size',
      'sizes',
      'span',
      'src',
      'srcdoc',
      'srclang',
      'srcset',
      'start',
      'step',
      'summary',
      'spellcheck',
      'style',
      'tabindex',
      'target',
      'title',
      'type',
      'translate',
      'usemap',
      'value',
      'valign',
      'webkitallowfullscreen',
      'width',
      'wrap',
    ],
    fontFamilyDefaultSelection: 'Open Sans, sans-serif',
    fontFamilySelection: true,
    pluginsDisabled: [
      'file',
      'filesManager',
      'filestack',
      'imageManager',
      ...disabledPlugins,
    ],
    toolbarButtons,
    quickInsertEnabled: toolbar === TOOLBAR_TYPES.LARGE,
    quickInsertButtons,
    fontSize: [
      '8',
      '9',
      '10',
      '11',
      '12',
      '14',
      '16',
      '18',
      '20',
      '22',
      '24',
      '30',
      '36',
      '48',
      '60',
      '72',
      '96',
    ],
    imageUploadURL,
    imageUploadMethod: 'POST',
    videoUploadURL,
    videoUploadMethod: 'POST',
    requestWithCredentials: true,
    ...options,
  };
}

const largeToolbarConfig = function (disabledPlugins: string[]) {
  return {
    moreText: {
      buttons: [
        'fullscreen',
        'undo',
        'redo',
        'paragraphFormat',
        'fontFamily',
        'fontSize',
        'bold',
        'italic',
        'underline',
        'textColor',
        'backgroundColor',
        'fontweight',
        'strikeThrough',
        'subscript',
        'superscript',
        'clearFormatting',
      ],
      buttonsVisible: 11,
    },
    moreParagraph: {
      buttons: [
        'alignLeft',
        'alignCenter',
        'lineHeight',
        'formatUL',
        'formatOL',
        'outdent',
        'indent',
        'alignRight',
        'alignJustify',
        'quote',
      ],
      buttonsVisible: 7,
    },
    moreRich: {
      buttons: [
        'insertHR',
        'templates',
        'accordion',
        'insertTable',
        'insertImage',
        'insertVideo',
        'insertLink',
        'emoticons',
        'specialCharacters',
      ].filter(btn => !disabledPlugins.some(p => p === btn)),
      buttonsVisible: 0,
    },
    moreMisc: {
      buttons: ['print', 'html', 'selectAll', 'help'],
      align: 'right',
      buttonsVisible: 0,
    },
    aiLearningContent: {
      buttons: ['aiLearningContent'],
      align: 'left',
      buttonsVisible: 0,
    },
    aiImprove: {
      buttons: ['aiImprove'],
      align: 'left',
      buttonsVisible: 0,
    },
    aiTranslate: {
      buttons: ['aiTranslate'],
      align: 'left',
      buttonsVisible: 0,
    },
  };
};

const iltEventToolbarConfig = {
  moreText: {
    buttons: [
      'undo',
      'redo',
      'paragraphFormat',
      'fontFamily',
      'fontSize',
      'bold',
      'italic',
      'underline',
      'textColor',
      'backgroundColor',
      'fontweight',
      'strikeThrough',
      'subscript',
      'superscript',
      'clearFormatting',
    ],
    buttonsVisible: 8,
  },
  moreParagraph: {
    buttons: [
      'alignLeft',
      'alignCenter',
      'formatUL',
      'formatOL',
      'alignRight',
      'alignJustify',
      'lineHeight',
      'outdent',
      'indent',
      'quote',
    ],
    buttonsVisible: 4,
  },
  moreRich: {
    buttons: ['insertHR', 'insertLink', 'emoticons', 'specialCharacters'],
    buttonsVisible: 0,
  },
  moreMisc: {
    buttons: ['fullscreen', 'print', 'html', 'selectAll', 'help'],
    buttonsVisible: 0,
  },
  aiTranslate: {
    buttons: ['aiTranslate'],
    buttonsVisible: 0,
  },
  aiImprove: {
    buttons: ['aiImprove'],
    buttonsVisible: 0,
  },
};

const smallToolbarConfig = {
  moreText: {
    buttons: [
      'bold',
      'italic',
      'underline',
      'formatUL',
      'formatOL',
      'insertLink',
      'clearFormatting',
    ],
    buttonsVisible: 8,
  },
};
